import styles from "./product.module.css";

import {
  forwardRef,
  useState,
  useEffect,
} from "react";

import { CiHeart } from "react-icons/ci";
import { FaHeart, FaBell } from "react-icons/fa";
import { LiaRupeeSignSolid } from "react-icons/lia";

import {
  HeadingTitle,
  Image,
} from "../../../shared/index";

import { Link } from "react-router-dom";

import { getImageUrl } from "../../../../lib/get-image";

import toast from "react-hot-toast";

import authService from "../../../../services/authService";

import {
  useAddToWishList,
  useRemoveWishList,
} from "../../../../queries/useWishListQuery";

import {
  useAddToCart,
  useIncreaseCartQty,
  useDecreaseCartQty,
  useGetCart,
} from "../../../../queries/useCartQuery";

import productStockNotificationService from "../../../../services/productNotifyService";


const Product = forwardRef(
  (
    {
      product,
      wishItems,
      isWishListPending,
    },
    ref
  ) => {
    const {
      id,
      name,
      slug,
      thumb_image,
      price,
      offer_price,
    } = product;

    const isLoggedIn = authService.isLoggedIn();

    /* =========================
       CART
    ========================= */

    const {
      data: cartItems = [],
    } = useGetCart();

    const {
      mutate: addToCart,
      isPending: isAdding,
    } = useAddToCart();

    useIncreaseCartQty();
    useDecreaseCartQty();

    const maxQty = product?.qty || 0;

    const cartItem = cartItems?.find(
      (item) => item.product_id === id
    );

    const isInCart = Boolean(cartItem);


    /* =========================
       WISHLIST
    ========================= */

    const {
      mutate: addWish,
    } = useAddToWishList();

    const {
      mutate: removeWish,
    } = useRemoveWishList();

    const wishItem = wishItems?.find(
      (w) => w.product_id === id
    );

    const inWishList = Boolean(wishItem);

    const wishlistId = wishItem?.id;


    const handleWishlist = (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (!isLoggedIn) {
        window.dispatchEvent(
          new Event("open-login-otp")
        );
        return;
      }

      if (inWishList) {
        removeWish(wishlistId);
      } else {
        addWish({
          product_id: id,
        });
      }
    };


    /* =========================
       PRICE
    ========================= */

    const originalPrice =
      Number(price) || 0;

    const offerPrice =
      Number(offer_price) || 0;

    const hasOffer =
      offerPrice > 0 &&
      offerPrice < originalPrice;

    const finalPrice = hasOffer
      ? offerPrice
      : originalPrice;

    const discountPercent =
      hasOffer
        ? Math.round(
            ((originalPrice - offerPrice) /
              originalPrice) *
              100
          )
        : 0;


    /* =========================
       QUANTITY
    ========================= */

    const [quantity, setQuantity] =
      useState(1);

    useEffect(() => {
      setQuantity(1);
    }, [id]);


    /* =========================
       INCREASE
    ========================= */

    const handleIncrease = (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (!isLoggedIn) {
        window.dispatchEvent(
          new Event("open-login-otp")
        );
        return;
      }

      setQuantity((prev) => {
        if (prev >= maxQty) {
          return prev;
        }

        return prev + 1;
      });
    };


    /* =========================
       DECREASE
    ========================= */

    const handleDecrease = (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (!isLoggedIn) {
        window.dispatchEvent(
          new Event("open-login-otp")
        );
        return;
      }

      setQuantity((prev) =>
        prev > 1 ? prev - 1 : 1
      );
    };


    /* =========================
       ADD TO CART
    ========================= */

    const handleAddToCart = (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (!isLoggedIn) {
        window.dispatchEvent(
          new Event("open-login-otp")
        );
        return;
      }

      if (isInCart) {
        toast.error("Already in cart");
        return;
      }

      addToCart(
        {
          product_id: id,
          quantity: quantity,
        },
        {
          onSuccess: () => {
            toast.success(
              "Item added to cart 🛒"
            );
          },
        }
      );
    };


    /* =====================================================
       NOTIFY ME STATES
    ===================================================== */

    const [showNotifyModal, setShowNotifyModal] =
      useState(false);

    const [notifyContact, setNotifyContact] =
      useState("");

    const [isNotifyPending, setIsNotifyPending] =
      useState(false);

    const [isNotified, setIsNotified] =
      useState(false);


    /* =====================================================
       NOTIFY ME
    ===================================================== */

    const handleNotifyMe = async (e) => {
      e?.preventDefault();
      e?.stopPropagation();

      const loggedIn =
        authService.isLoggedIn();

      /* ==========================================
         LOGGED IN USER
         Direct API call
      ========================================== */

      if (loggedIn) {
        try {
          setIsNotifyPending(true);

          const response =
            await productStockNotificationService.subscribe({
              productId: id,
            });

          toast.success(
            response?.message ||
              "You will be notified when this product is back in stock."
          );

          setIsNotified(true);

        } catch (error) {
          console.error(
            "Notify API Error:",
            error
          );

          toast.error(
            error?.message ||
              "Unable to subscribe for notification"
          );

        } finally {
          setIsNotifyPending(false);
        }

        return;
      }


      /* ==========================================
         GUEST USER
         Open popup
      ========================================== */

      setNotifyContact("");
      setShowNotifyModal(true);
    };


    /* =====================================================
       GUEST NOTIFY SUBMIT
    ===================================================== */

    const handleGuestNotifySubmit = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const value =
        notifyContact.trim();


      /* ==========================================
         EMPTY
      ========================================== */

      if (!value) {
        toast.error(
          "Please enter your email or phone number"
        );
        return;
      }


      /* ==========================================
         EMAIL VALIDATION
      ========================================== */

      const isEmail =
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          value
        );


      /* ==========================================
         PHONE VALIDATION
         Exactly 10 digits
      ========================================== */

      const isPhone =
        /^[0-9]{10}$/.test(value);


      /* ==========================================
         INVALID
      ========================================== */

      if (!isEmail && !isPhone) {
        toast.error(
          "Please enter a valid email or 10-digit phone number"
        );
        return;
      }


      /* ==========================================
         API
      ========================================== */

      try {
        setIsNotifyPending(true);

        const response =
          await productStockNotificationService.subscribe({
            productId: id,
            email: isEmail
              ? value
              : undefined,
            phone: isPhone
              ? value
              : undefined,
          });


        /* ==========================================
           SUCCESS
        ========================================== */

        toast.success(
          response?.message ||
            "You will be notified when this product is back in stock."
        );

        setIsNotified(true);

        setShowNotifyModal(false);

        setNotifyContact("");

      } catch (error) {
        console.error(
          "Guest Notify API Error:",
          error
        );

        toast.error(
          error?.message ||
            "Unable to subscribe for notification"
        );

      } finally {
        setIsNotifyPending(false);
      }
    };


    /* =====================================================
       RENDER
    ===================================================== */

    return (
      <>
        <div
          ref={ref}
          className={styles.productCard}
        >

          {/* =========================
              IMAGE
          ========================= */}

          <Link
            to={`/productdetails/${slug}`}
          >
            <div
              className={
                styles.productImageWrapper
              }
            >

              <button
                type="button"
                className={
                  styles.wishlistButton
                }
                onClick={handleWishlist}
                disabled={
                  isWishListPending
                }
              >
                {inWishList ? (
                  <FaHeart
                    size={22}
                    color="#e53935"
                  />
                ) : (
                  <CiHeart
                    size={22}
                    color="#444"
                  />
                )}
              </button>


              <Image
                src={getImageUrl(
                  thumb_image
                )}
                alt={name}
                className={
                  styles.productImage
                }
              />

            </div>


            {/* =========================
                PRODUCT NAME
            ========================= */}

            <div
              className={
                styles.productInfo
              }
            >
              <HeadingTitle
                text={name}
                style={
                  styles.productName
                }
              />
            </div>

          </Link>


          {/* =========================
              PRICE
          ========================= */}

          <div
            className={
              styles.priceWrapper
            }
          >
            <div
              className={
                styles.priceBlock
              }
            >
              <div
                className={
                  styles.priceRow
                }
              >

                <p
                  className={
                    styles.productPrice
                  }
                >
                  <LiaRupeeSignSolid />
                  {finalPrice}
                </p>


                {hasOffer && (
                  <p
                    className={
                      styles.originalPrice
                    }
                  >
                    <LiaRupeeSignSolid />
                    {originalPrice}
                  </p>
                )}


                {hasOffer && (
                  <span
                    className={
                      styles.discountTextRight
                    }
                  >
                    {discountPercent}% OFF
                  </span>
                )}

              </div>
            </div>
          </div>


          {/* =========================
              CART / NOTIFY
          ========================= */}

          <div
            className={
              styles.cartSection
            }
          >

            {maxQty > 0 ? (
              <>

                {/* QUANTITY */}

                <div
                  className={
                    styles.qtyBox
                  }
                >

                  <div
                    className={
                      styles.qtyValue
                    }
                  >
                    {quantity}
                  </div>


                  <div
                    className={
                      styles.qtyArrows
                    }
                  >

                    <button
                      type="button"
                      className={
                        styles.arrowBtn
                      }
                      onClick={
                        handleIncrease
                      }
                      disabled={
                        isInCart ||
                        quantity >= maxQty
                      }
                    >
                      ▲
                    </button>


                    <button
                      type="button"
                      className={
                        styles.arrowBtn
                      }
                      onClick={
                        handleDecrease
                      }
                      disabled={
                        isInCart
                      }
                    >
                      ▼
                    </button>

                  </div>

                </div>


                {/* ADD TO CART */}

                <button
                  type="button"
                  className={
                    styles.addToCartBtn
                  }
                  onClick={
                    handleAddToCart
                  }
                  disabled={
                    isAdding ||
                    isInCart
                  }
                >
                  {isInCart
                    ? "Add To Cart 🛒"
                    : isAdding
                    ? "Adding..."
                    : "Add To Cart 🛒"}
                </button>

              </>
            ) : (

              /* =========================
                 OUT OF STOCK
              ========================= */

              <button
                type="button"
                className={
                  styles.addToCartBtn
                }
                onClick={
                  handleNotifyMe
                }
                disabled={
                  isNotifyPending ||
                  isNotified
                }
              >
                {isNotifyPending
                  ? "Subscribing..."
                  : isNotified
                  ? "Notification Enabled"
                  : "Notify Me"}
              </button>

            )}

          </div>

        </div>


        {/* =================================================
            GUEST NOTIFY POPUP
        ================================================= */}

        {showNotifyModal && (
          <div
            className={
              styles.notifyModalOverlay
            }
            onClick={() =>
              setShowNotifyModal(false)
            }
          >

            <div
              className={
                styles.notifyModal
              }
              onClick={(e) =>
                e.stopPropagation()
              }
            >

              {/* CLOSE */}

              <button
                type="button"
                className={
                  styles.notifyClose
                }
                onClick={() =>
                  setShowNotifyModal(false)
                }
              >
                ×
              </button>


              {/* ICON */}

              <div
                className={
                  styles.notifyIcon
                }
              >
                <FaBell size={24} />
              </div>


              {/* TITLE */}

              <h3>
                Get Notified
              </h3>


              {/* DESCRIPTION */}

              <p>
                This product is currently
                out of stock.
                <br />
                Enter your email or phone
                number and we'll notify you
                when it is back in stock.
              </p>


              {/* FORM */}

              <form
                onSubmit={
                  handleGuestNotifySubmit
                }
              >

                <input
                  type="text"
                  value={
                    notifyContact
                  }
                  onChange={(e) =>
                    setNotifyContact(
                      e.target.value
                    )
                  }
                  placeholder="Enter email or phone number"
                  className={
                    styles.notifyInput
                  }
                  autoFocus
                />


                <button
                  type="submit"
                  className={
                    styles.notifySubmitBtn
                  }
                  disabled={
                    isNotifyPending
                  }
                >
                  <FaBell size={16} />

                  {isNotifyPending
                    ? "Submitting..."
                    : "Notify Me"}
                </button>

              </form>

            </div>

          </div>
        )}

      </>
    );
  }
);

export default Product;